import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import ConferenceRequestForm from '../components/ConferenceRequestForm/ConferenceRequestForm';
import { AppDispatch } from '../store/store';
import conferenceSlice from '../store/conference/slice';
import { validationErrorTransformer } from '../store/api';
import { AppState } from '../store/state';
import { ValidatedField } from '../store/api.d';
import { selectConference, selectCurrentUser } from '../store/selectors';
import styled, { ThemeProvider } from 'styled-components';
import useTheme from '@material-ui/core/styles/useTheme';
import { useTranslation } from 'react-i18next';
import { ConferenceRequestFormFields } from '../store/conference/types';
import WizardCard from '../components/ui/surfaces/Card/WizardCard';
import Success from '../components/Success/Success';
import history from '../store/history';
import { getRouteByName } from '../router/routes';

interface ConferenceCreatePageProps {
  request: {
    loading: boolean;
    data?: ConferenceRequestFormFields;
  };
  timezone: string | null;
  dateFormat: string | null;
  validationError?: ValidatedField[];
  postConferenceRequestAction: (data: ConferenceRequestFormFields) => void;
  clearValidationErrors: () => void;
  clearConferenceRequestAction: () => void;
}

const ConferencesCreatePage: React.FC<ConferenceCreatePageProps> = ({
  request,
  timezone,
  dateFormat,
  validationError,
  postConferenceRequestAction,
  clearValidationErrors,
  clearConferenceRequestAction,
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    return () => {
      // This function clears the current request at exit
      clearConferenceRequestAction();
    };
  }, []);

  const { loading, data: requestData } = request;

  const initConference: ConferenceRequestFormFields = {
    id: 0,
    name: '',
    short_name: '',
    start_date: '',
    end_date: '',
    location: '',
    url: '',
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="h-full flex flex-col justify-center mobileCtrlRequestConferenceFormWrapper">
        {requestData ? (
          <Success
            title={t('Successful request')}
            message={t(
              'We have received your conference request successfully. You will get a response by email in the next 48 hours.',
            )}
            buttonText={t('Back to ChairingTool')}
            handleClick={() => history.push(getRouteByName('RouteConferences').path)}
          />
        ) : (
          <div className="flex flex-col items-center mb-8">
            <StyledTitle>{t('Request a conference')}</StyledTitle>
            <StyledText className="mb-14">
              {t('Send us a request to host your conference at ') + 'chairing' + 'tool.'}
            </StyledText>

            <WizardCard>
              <ConferenceRequestForm
                loading={loading}
                timezone={timezone}
                dateFormat={dateFormat}
                initConference={initConference}
                validation_error={
                  validationError
                    ? (validationErrorTransformer(validationError) as unknown as ConferenceRequestFormFields)
                    : undefined
                }
                save_handler={postConferenceRequestAction}
                clearValidationErrors={clearValidationErrors}
              />
            </WizardCard>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
};

const StyledTitle = styled.h2`
  font-size: 2.8rem;
  font-weight: 700;
  text-align: center;
`;
const StyledText = styled.p`
  font-size: 1.1rem;
  text-align: center;
  margin-top: 0.5rem;
`;

const mapStateToProps = (state: AppState) => ({
  request: selectConference(state).request,
  timezone: selectCurrentUser(state).person.timezone,
  dateFormat: selectCurrentUser(state).person.date_format,
  validationError: selectConference(state).validationError,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  postConferenceRequestAction: (data: ConferenceRequestFormFields) =>
    dispatch(conferenceSlice.actions.POST_CONFERENCE_REQUEST(data)),
  clearValidationErrors: () => dispatch(conferenceSlice.actions['CLEAR_VALIDATION_ERRORS']()),
  clearConferenceRequestAction: () => dispatch(conferenceSlice.actions['CLEAR_CONFERENCE_REQUEST']()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ConferencesCreatePage);
